import { createSlice } from '@reduxjs/toolkit';
import {
    setSessionDataWithExpiry,
    getSessionDataWithValidation,
    clearSessionData
} from '../../hooks/commonFunction';

// Encrypt sensitive data (basic obfuscation)
const encryptData = (data) => {
    try {
        return btoa(JSON.stringify(data));
    } catch {
        return null;
    }
};

const decryptData = (encryptedData) => {
    try {
        return JSON.parse(atob(encryptedData));
    } catch {
        return null;
    }
};

const SESSION_KEY = 'hf_dp_secure';

const initialState = {
    isDeliveryPartnerMode: false,
    partnerInfo: null,
    userInfo: null,
    sessionId: null,
    lastUpdated: null,
};

const deliveryPartnerSlice = createSlice({
    name: 'deliveryPartner',
    initialState,
    reducers: {
        setPartnerInfo: (state, action) => {
            const { partnerData, userData = null } = action.payload;

            state.isDeliveryPartnerMode = true;
            state.partnerInfo = {
                shortName: partnerData.shortName,
                partnerId: partnerData.partnerId,
                name: partnerData.name,
                address: partnerData.address,
            };
            state.userInfo = userData ? {
                userId: userData.userId,
                userRole: userData.userRole,
                phone: userData.phone,
                // Preserve existing custom_notes if they exist
                ...(state.userInfo?.custom_notes && { custom_notes: state.userInfo.custom_notes }),
            } : state.userInfo; // Keep existing userInfo if no userData provided
            state.sessionId = `dp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            state.lastUpdated = Date.now();

            // Backup to encrypted session storage
            const sessionData = {
                isDeliveryPartnerMode: true,
                partnerInfo: state.partnerInfo,
                userInfo: state.userInfo,
                sessionId: state.sessionId,
                lastUpdated: state.lastUpdated,
            };

            const encrypted = encryptData(sessionData);
            if (encrypted) {
                setSessionDataWithExpiry(SESSION_KEY, encrypted, 3600000); // 1 hour
            }
        },

        restoreFromSession: (state) => {
            const encryptedData = getSessionDataWithValidation(SESSION_KEY);
            if (encryptedData) {
                const decrypted = decryptData(encryptedData);
                if (decrypted && decrypted.sessionId) {
                    state.isDeliveryPartnerMode = decrypted.isDeliveryPartnerMode;
                    state.partnerInfo = decrypted.partnerInfo;
                    state.userInfo = decrypted.userInfo;
                    state.sessionId = decrypted.sessionId;
                    state.lastUpdated = decrypted.lastUpdated;
                }
            }
        },

        clearPartnerInfo: (state) => {
            state.isDeliveryPartnerMode = false;
            state.partnerInfo = null;
            state.userInfo = null;
            state.sessionId = null;
            state.lastUpdated = null;

            // Clear session storage
            clearSessionData(SESSION_KEY);
        },

        validateSession: (state) => {
            // Check if session is still valid (within 1 hour)
            if (state.lastUpdated && Date.now() - state.lastUpdated > 3600000) {
                // Session expired, clear everything
                state.isDeliveryPartnerMode = false;
                state.partnerInfo = null;
                state.userInfo = null;
                state.sessionId = null;
                state.lastUpdated = null;
                clearSessionData(SESSION_KEY);
            }
        },

        updateCustomNotes: (state, action) => {
            // Create userInfo if it doesn't exist
            if (!state.userInfo) {
                state.userInfo = {};
            }

            state.userInfo.custom_notes = action.payload;
            state.lastUpdated = Date.now();
            // Update session storage with new notes
            const sessionData = {
                isDeliveryPartnerMode: state.isDeliveryPartnerMode,
                partnerInfo: state.partnerInfo,
                userInfo: state.userInfo,
                sessionId: state.sessionId,
                lastUpdated: state.lastUpdated,
            };

            const encrypted = encryptData(sessionData);
            if (encrypted) {
                setSessionDataWithExpiry(SESSION_KEY, encrypted, 3600000); // 1 hour
            }
        }
    },
});

export const {
    setPartnerInfo,
    restoreFromSession,
    clearPartnerInfo,
    validateSession,
    updateCustomNotes
} = deliveryPartnerSlice.actions;

// Selectors
export const selectIsDeliveryPartnerMode = (state) => state.deliveryPartner.isDeliveryPartnerMode;
export const selectPartnerInfo = (state) => state.deliveryPartner.partnerInfo;
export const selectDeliveryPartnerUserInfo = (state) => state.deliveryPartner.userInfo;
export const selectSessionId = (state) => state.deliveryPartner.sessionId;

export default deliveryPartnerSlice.reducer;